import { useEffect, useRef, type ReactNode } from 'react';
import { ASCII_ALPHA, ASCII_SPEED, isCustomPattern, useAscii, type BuiltinPattern } from '../store/ascii.js';

/** Від легкого до щільного; символ у межах рівня в клітинки свій і постійний. */
const LEVELS = [
  ['.', '·', '`', ','],
  [':', ';', '-', '~', '"'],
  ['=', '+', '*', '^', '<', '>'],
  ['#', '%', '&', '$', '?', '/'],
  ['@', 'W', 'M', '8', 'X', '0'],
];
const CELL = 14;
/**
 * Під склом кожен новий кадр — перерахунок розмиття плиток (див. base.css),
 * тож малюємо рідко: хвилі повільні, 12 кадрів/с їх не видають.
 */
const FRAME_MS = 1000 / 12;
const ALPHA_STEPS = 6;

/** Символ клітинки постійний і не залежить від полотна: два шари фону збігаються літера в літеру. */
function glyphSeed(i: number, j: number): number {
  return ((Math.imul(i, 73856093) ^ Math.imul(j, 19349663)) >>> 0) % 251;
}

/** Густота клітинки 0…1 у мить t; поле на всю сітку cols×rows. */
type Field = (i: number, j: number, t: number, cols: number, rows: number) => number;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Хвилі, що перетинаються і розходяться від середини. */
const waves: Field = (x, y, t, cols, rows) => {
  const v =
    Math.sin(x * 0.045 + t * 0.9) +
    Math.sin(y * 0.06 - t * 0.7) +
    Math.sin((x + y) * 0.03 + t * 0.5) +
    Math.sin(Math.hypot(x - cols / 2, y - rows / 2) * 0.12 - t * 1.2);
  return ((v + 4) / 8) ** 3;
};

/** Стовпчики символів стікають донизу: яскрава голова, плавний хвіст, у кожному проході свій. */
const rain: Field = (i, j, t, _cols, rows) => {
  const h = glyphSeed(i, 7);
  if (h % 6 === 0) return 0;
  const speed = 9 + (h % 11) * 1.6;
  const tail = 10 + (h % 16);
  const period = rows + tail + 6 + (h % 17);
  const clock = t * speed + h * 13;
  // Номер проходу: після кожного кола стовпчик то зникає, то біжить з іншим зсувом.
  const pass = Math.floor(clock / period);
  const gate = glyphSeed(i, pass + 31);
  if (gate % 8 === 0) return 0;
  const head = (clock % period) + (gate % 7) * 0.4;
  const d = head - j;
  if (d < 0 || d > tail) return 0;
  if (d < 1) return 1;
  const fall = (1 - d / tail) ** 1.8;
  // Хвіст ледь мерехтить, як у старих терміналах.
  const shimmer = 0.82 + 0.18 * Math.sin(t * 6 + j * 1.7 + h);
  return fall * shimmer * 0.8;
};

/** Краплі падають у різних місцях, від кожної біжить затухаюче кільце. */
const ripples: Field = (i, j, t, cols, rows) => {
  const life = 7;
  let v = 0;
  for (let k = 0; k < 5; k++) {
    const n = Math.floor(t / life + k * 0.37);
    const age = t - (n - k * 0.37) * life;
    if (age < 0 || age > life) continue;
    const cx = cols * (0.1 + 0.8 * (glyphSeed(n, k * 11 + 1) / 250));
    const cy = rows * (0.1 + 0.8 * (glyphSeed(n, k * 11 + 2) / 250));
    const d = Math.hypot((i - cx) * 0.55, (j - cy) * 0.55 * 1.0) * 1.8;
    const radius = age * 7;
    const fade = (1 - age / life) ** 1.4;
    const ring = Math.exp(-((d - radius) ** 2) / 5);
    const echo = 0.45 * Math.exp(-((d - radius * 0.62) ** 2) / 9) * (age > 0.8 ? 1 : 0);
    v += (ring + echo) * fade;
  }
  return clamp01(v) ** 1.2;
};

/** Завіси полярного сяйва: три шари, промені вертикальними смугами, плавне згасання донизу. */
const aurora: Field = (i, j, t, _cols, rows) => {
  let v = 0;
  for (let k = 0; k < 3; k++) {
    const base = rows * (0.22 + k * 0.2);
    const yc =
      base +
      rows * 0.09 * Math.sin(i * 0.03 + t * (0.35 + k * 0.15) + k * 2.4) +
      rows * 0.04 * Math.sin(i * 0.09 - t * 0.7 + k);
    const dy = j - yc;
    const spread = dy < 0 ? 1.4 : 6 + 5 * (0.5 + 0.5 * Math.sin(i * 0.05 + t * 0.3 + k * 2));
    const body = Math.exp(-(dy * dy) / (2 * spread * spread));
    // Промені: вузькі вертикальні смуги, що пливуть уздовж завіси.
    const rays = 0.5 + 0.5 * Math.sin(i * 0.5 + Math.sin(i * 0.07 + t * 0.5 + k) * 3 - t * 0.9);
    const breathe = 0.6 + 0.4 * Math.sin(i * 0.02 - t * 0.5 + k * 4);
    v += body * (0.55 + 0.45 * rays) * breathe;
  }
  return clamp01(v * 0.8) ** 1.4;
};

/** Зорі мерехтять, а час від часу небом пролітає метеор із хвостом. */
const stars: Field = (i, j, t, cols, rows) => {
  let v = 0;
  const s = ((Math.imul(i + 11, 2654435761) ^ Math.imul(j + 3, 40503)) >>> 0) % 997;
  if (s % 8 === 0) {
    const rate = 0.7 + (s % 7) * 0.3;
    const tw = 0.5 + 0.5 * Math.sin(t * rate * 2.5 + s);
    v = tw ** 2.5 * (s % 5 === 0 ? 1 : 0.65);
  }
  const span = 9;
  const n = Math.floor(t / span);
  const age = t - n * span;
  const sx = cols * (0.1 + 0.6 * (glyphSeed(n, 5) / 250));
  const sy = rows * (0.05 + 0.3 * (glyphSeed(n, 6) / 250));
  const run = age * 22;
  const dx = i - (sx + run);
  const dy = j - (sy + run * 0.45);
  const along = -(dx * 0.91 + dy * 0.41);
  const across = Math.abs(dx * -0.41 + dy * 0.91);
  if (along >= 0 && along < 14 && across < 0.9 && age < 4) {
    v = Math.max(v, (1 - along / 14) ** 1.5 * (1 - across));
  }
  return v;
};

/** Дві галактичні спіралі обертаються назустріч, у центрі ядро. */
const vortex: Field = (i, j, t, cols, rows) => {
  const dx = (i - cols / 2) * 0.55;
  const dy = j - rows / 2;
  const r = Math.hypot(dx, dy);
  const a = Math.atan2(dy, dx);
  const reach = Math.max(cols * 0.55, rows) * 0.6;
  const arms = 0.5 + 0.5 * Math.sin(a * 2 - Math.log(r + 1) * 3.2 + t * 0.9);
  const inner = 0.5 + 0.5 * Math.sin(a * 3 + r * 0.35 - t * 1.4);
  const fade = Math.exp(-((r / reach) ** 2) * 1.6);
  const core = Math.exp(-(r * r) / 14);
  return clamp01(((arms ** 2.4) * 0.8 + (inner ** 3) * 0.25) * fade * Math.min(1, r / 3) + core * 0.9);
};

const FIELDS: Record<BuiltinPattern, Field> = { waves, rain, ripples, aurora, stars, vortex };

/**
 * Свій HTML-фон. Сторінка-оболонка `/bg-frame.html` лежить у фреймі без
 * доступу до програми й сама малює те, що їй надішлють, — політика безпеки
 * програми забороняла б вбудовані скрипти, а для цієї оболонки вона окрема.
 */
function CustomFrame({ html, opacity }: { html: string; opacity: number }): ReactNode {
  const ref = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    const send = (): void => frame.contentWindow?.postMessage(html, '*');
    const onMsg = (e: MessageEvent): void => {
      if (e.source === frame.contentWindow && e.data === 'bg-ready') send();
    };
    window.addEventListener('message', onMsg);
    // Оболонка могла завантажитись раніше за цей ефект.
    send();
    return () => window.removeEventListener('message', onMsg);
  }, [html]);
  return (
    <iframe
      ref={ref}
      className="ambient-ascii ambient-custom"
      sandbox="allow-scripts"
      src="/bg-frame.html"
      title="Свій фон"
      tabIndex={-1}
      style={{ opacity }}
    />
  );
}

/** Фон із символів, що візерунком то проступають, то тануть. Лише canvas. */
export function AsciiWave(): ReactNode {
  const ref = useRef<HTMLCanvasElement>(null);
  const on = useAscii(s => s.on);
  // Зміна візерунка перезапускає полотно: нерухомий фон теж перемальовується.
  const pattern = useAscii(s => s.pattern);
  const customs = useAscii(s => s.customs);
  const strength = useAscii(s => s.strength);

  useEffect(() => {
    if (!on || isCustomPattern(pattern)) return;
    const cv = ref.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cols = 0;
    let rows = 0;
    let color = '#fff';
    let raf = 0;
    let last = 0;
    let colorAt = 0;

    const readColor = (): void => {
      color = getComputedStyle(document.documentElement).getPropertyValue('--text').trim() || '#fff';
    };

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `${CELL}px ui-monospace, Menlo, Consolas, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      cols = Math.ceil(w / CELL);
      rows = Math.ceil(h / CELL);
    };

    const draw = (t: number): void => {
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.fillStyle = color;
      const field = FIELDS[useAscii.getState().pattern as BuiltinPattern] ?? waves;
      let lastStep = -1;
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const v = field(i, j, t, cols, rows);
          const k = Math.floor(v * (LEVELS.length + 0.3)) - 1;
          if (k < 0) continue;
          const step = Math.max(1, Math.round(v * ALPHA_STEPS));
          if (step !== lastStep) {
            ctx.globalAlpha = (step / ALPHA_STEPS) * ASCII_ALPHA[useAscii.getState().strength];
            lastStep = step;
          }
          const set = LEVELS[Math.min(k, LEVELS.length - 1)] ?? LEVELS[0]!;
          const ch = set[glyphSeed(i, j) % set.length] ?? '.';
          ctx.fillText(ch, i * CELL + CELL / 2, j * CELL + CELL / 2);
        }
      }
    };

    const frame = (now: number): void => {
      raf = requestAnimationFrame(frame);
      if (now - last < FRAME_MS) return;
      last = now;
      // Кольори курсу можуть прийти вже після запуску — перечитуємо раз на секунду.
      if (now - colorAt > 1000) {
        colorAt = now;
        readColor();
      }
      draw((now / 1000) * ASCII_SPEED[useAscii.getState().speed]);
    };

    const start = (): void => {
      if (still || raf) return;
      raf = requestAnimationFrame(frame);
    };
    const stop = (): void => {
      cancelAnimationFrame(raf);
      raf = 0;
    };
    const onVisibility = (): void => (document.hidden ? stop() : start());
    const onResize = (): void => {
      resize();
      if (still) draw(8);
    };

    readColor();
    resize();
    if (still) draw(8);
    else start();

    const themeObserver = new MutationObserver(() => {
      readColor();
      if (still) draw(8);
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      themeObserver.disconnect();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [on, pattern]);

  if (!on) return null;
  if (isCustomPattern(pattern)) {
    const html = customs.find(c => `custom:${c.id}` === pattern)?.html;
    // key: document.open() у фреймі стирає його слухача, тож другий фон тією самою
    // оболонкою не прийняти — кожному потрібен свіжий фрейм.
    return html ? <CustomFrame key={`${pattern}:${html.length}`} html={html} opacity={Math.min(1, ASCII_ALPHA[strength] * 2)} /> : null;
  }
  return <canvas ref={ref} className="ambient-ascii" />;
}
